/**
 * Content Grid Item Component
 * Displays a content item as a card in grid view
 */

import { useState } from "react";
import type { ContentItem } from "../../types/content.types";
import { ContentType } from "../../types/content.types";
import {
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  PaperClipIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

interface ContentGridItemProps {
  item: ContentItem;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onClick: () => void;
  onDelete: () => void;
  onShare?: () => void;
}

export default function ContentGridItem({
  item,
  selected,
  onSelect,
  onClick,
  onDelete,
  onShare,
}: ContentGridItemProps) {
  const [showMenu, setShowMenu] = useState(false);

  const getTypeIcon = () => {
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return <DocumentIcon className="h-8 w-8" />;
      case ContentType.IMAGE:
        return <PhotoIcon className="h-8 w-8" />;
      case ContentType.VIDEO_LINK:
        return <VideoCameraIcon className="h-8 w-8" />;
      case ContentType.TEXT_BLOCK:
        return <DocumentTextIcon className="h-8 w-8" />;
      case ContentType.FILE:
        return <PaperClipIcon className="h-8 w-8" />;
      default:
        return <DocumentIcon className="h-8 w-8" />;
    }
  };

  const getTypeLabel = () => {
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return "Flipbook";
      case ContentType.IMAGE:
        return "Image";
      case ContentType.VIDEO_LINK:
        return "Video";
      case ContentType.TEXT_BLOCK:
        return "Text";
      case ContentType.FILE:
        return "File";
      default:
        return "Unknown";
    }
  };

  const getTypeColor = () => {
    switch (item.content_type) {
      case ContentType.FLIPBOOK:
        return "bg-purple-100 text-purple-700";
      case ContentType.IMAGE:
        return "bg-green-100 text-green-700";
      case ContentType.VIDEO_LINK:
        return "bg-red-100 text-red-700";
      case ContentType.TEXT_BLOCK:
        return "bg-blue-100 text-blue-700";
      case ContentType.FILE:
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div
      className={clsx(
        "group relative bg-white rounded-lg border transition-all cursor-pointer",
        selected
          ? "border-blue-500 ring-2 ring-blue-200"
          : "border-gray-200 hover:border-gray-300 hover:shadow-md",
      )}
    >
      {/* Selection Checkbox */}
      <div
        className={clsx(
          "absolute top-2 left-2 z-10 transition-opacity",
          selected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(e.target.checked);
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>

      {/* Menu Button */}
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className={clsx(
            "p-1 rounded-lg transition-colors",
            showMenu
              ? "bg-gray-100"
              : "opacity-0 group-hover:opacity-100 hover:bg-gray-100",
          )}
        >
          <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                  setShowMenu(false);
                }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg flex items-center gap-2"
              >
                <EyeIcon className="h-4 w-4" />
                View Details
              </button>
              {onShare && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <ShareIcon className="h-4 w-4" />
                  Share
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-b-lg flex items-center gap-2"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Featured Badge */}
      {item.is_featured && (
        <div className="absolute top-2 left-8 z-10" title="Featured">
          <svg
            className="h-5 w-5 text-yellow-500 drop-shadow"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </div>
      )}

      {/* Thumbnail */}
      <div
        className="aspect-[4/3] bg-gray-100 rounded-t-lg overflow-hidden"
        onClick={onClick}
      >
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            {getTypeIcon()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3" onClick={onClick}>
        {/* Title */}
        <h3 className="font-medium text-gray-900 truncate" title={item.title}>
          {item.title}
        </h3>

        {/* Meta */}
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className={clsx("px-2 py-0.5 rounded-full", getTypeColor())}>
            {getTypeLabel()}
          </span>
          <div className="flex items-center gap-2 text-gray-500">
            {item.is_public ? (
              <EyeIcon className="h-4 w-4" title="Public" />
            ) : (
              <EyeSlashIcon className="h-4 w-4" title="Private" />
            )}
            {item.view_count !== undefined && item.view_count > 0 && (
              <span>{item.view_count} views</span>
            )}
          </div>
        </div>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
              >
                {tag}
              </span>
            ))}
            {item.tags.length > 3 && (
              <span className="px-1.5 py-0.5 text-xs text-gray-400">
                +{item.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
